import { Box, Divider, Flex, Group, IconFont, Input, Popover, Text, Tooltip } from '@byecode/ui'
import type { ApplicationSettingAuthentication, ApplicationSettingAuthenticationCommon, VariableADTvalue } from '@lighthouse/core'
import { ApplicationSettingPerfectMode, BACKGROUND_TYPE, SpaceVersion, withEnableRegisterDefault } from '@lighthouse/core'
import type { AppRole } from '@lighthouse/shared'
import {
    fieldConvertValue,
    FilledInput,
    FillPickerPopoverDropdown,
    getImageFullUrlInApplication,
    ListItem4ByecodeUi,
    PopoverDropdownWithClose,
    spaceVersionEnum,
    UploadManageContext,
    useApplicationContext,
    useAtomAction,
    useAtomData,
    useSpaceQuotaContext
} from '@lighthouse/shared'
import { ScrollArea } from '@mantine/core'
import { clone, mergeDeepRight, reduce } from 'rambda'
import React, { useCallback, useEffect, useId, useMemo } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useUpdateEffect } from 'react-use'
import styled from 'styled-components'
import { debounce } from 'throttle-debounce'

import { fetchAppSettingAtom, updateAppVersionConfigAtom } from '@/atoms/application/action'
import { applicationSettingAtom } from '@/atoms/application/state'
import { AppSmsCard } from '@/components/AppSmsCard'
import { SwitchControl } from '@/components/BlockSettings/Common/SwitchControl'
import { openSpaceGradeConfirm, SpaceGradeTag } from '@/components/SpaceGrade'
import { TagIcon } from '@/components/TagIcon'
import { useCurrentAppId, useLanguage } from '@/hooks/useApplication'
import { useColorSystemAction } from '@/hooks/useColorSystemAction'
import { useDataSourceRoles } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useParseVariableWithSubscription } from '@/hooks/useParseVariable'
import { tokenRefreshManager } from '@/http'
import { uploadManagerInAppParams } from '@/utils/auth'

import SettingBlock from '../SettingBlock'
import * as CM from '../styles'
import { LoginBlock } from './LoginBlock'
import { LoginWeChat } from './LoginWeChat'
import Logo from './Logo'
import Name from './Name'
import PerfectFieldDetail from './PerfectFieldDetail'
import PerfectGroupSetting from './PerfectGroupSetting'
import PerfectWay from './PerfectWay'

const SCxContainer = styled.form`
    width: 360px;
    /* padding: 20px 12px; */
    flex-shrink: 0;
    height: 100%;
    max-height: 100%;
    background-color: #fff;
    font-size: var(--font-size-normal);
`
const SCxWrapper = styled(ScrollArea)`
    width: 100%;

    .mantine-ScrollArea-viewport {
        padding-bottom: 100px;
        > div {
            display: block !important;
        }
    }
`

interface SettingAuthenticationProps {}

const SettingAuthentication: React.FC<SettingAuthenticationProps> = () => {
    const authentication = useAtomData(
        applicationSettingAtom,
        useCallback(s => s?.authentication, [])
    )
    const appId = useCurrentAppId()
    const { run: updateAppVersionConfig } = useAtomAction(updateAppVersionConfigAtom)
    const { run: fetchAppSetting } = useAtomAction(fetchAppSettingAtom)
    const { quota } = useSpaceQuotaContext()

    const { roleOptions } = useApplicationContext()

    const newAuthentication: ApplicationSettingAuthentication | undefined = useMemo(() => {
        if (!authentication) {
            return
        }
        const roleSetting = authentication?.perfect.roleSetting ?? []
        const newRoleSetting = reduce<AppRole, ApplicationSettingAuthenticationCommon[]>(
            (p, c) => {
                const setting = roleSetting.find(v => v.roleId === c.id)
                const emptySetting: ApplicationSettingAuthenticationCommon = { roleId: c.id, fields: [], isShown: false }
                return setting ? [...p, setting] : [...p, emptySetting]
            },
            [],
            roleOptions
        )
        return withEnableRegisterDefault(
            mergeDeepRight<ApplicationSettingAuthentication>(authentication, { perfect: { roleSetting: newRoleSetting } })
        )
    }, [authentication, roleOptions])

    const methods = useForm<ApplicationSettingAuthentication>({
        mode: 'onChange',
        defaultValues: newAuthentication
    })

    const { getValues, register, reset, setValue, watch, control } = methods

    const [perfectMode, roleSetting = [], logo, background] = watch(['perfect.mode', 'perfect.roleSetting', 'logo', 'background'])

    const uploadId = useId()
    const uploadProps = useMemo(
        () => ({
            accept: 'image/*',
            multiple: false,
            uploadOptions: {
                info: { id: uploadId, label: '登录页面', groupId: 'loginPage' },
                options: {
                    ...uploadManagerInAppParams()
                }
            }
        }),
        [uploadId]
    )
    const parseVariable = useParseVariableWithSubscription(background?.image?.resource)
    const parseVariable2String = useCallback(
        (value: VariableADTvalue) => {
            const field = parseVariable(value)
            if (!field) {
                return ''
            }

            return fieldConvertValue(field, { useFileUrl: true })
        },
        [parseVariable]
    )

    const colorSystemMethods = useColorSystemAction()

    const roles = useDataSourceRoles()

    const language = useLanguage()

    const roleIds = useMemo(
        () =>
            roles
                .map(role => role.id)
                .sort((a, b) => a.localeCompare(b))
                .join(','),
        [roles]
    )

    // 监听角色删除添加时更新自定义应用注册设置
    const getAppSetting = useCallback(
        async (isChange?: boolean) => {
            const data = await fetchAppSetting()
            const newConfigGroupIds = (data.config.authentication.perfect.roleSetting ?? [])
                .map(setting => setting.roleId ?? '')
                ?.sort((a, b) => a.localeCompare(b))
                .join(',')
            const isEqualGroupLength = newConfigGroupIds === roleIds
            if (isEqualGroupLength || isChange) {
                reset(withEnableRegisterDefault(data.config.authentication))
            }
        },
        [fetchAppSetting, roleIds, reset]
    )

    useUpdateEffect(() => {
        getAppSetting(true)
    }, [language])

    useEffect(() => {
        reset(newAuthentication)
    }, [newAuthentication, reset])

    const handleSubmit = useMemo(
        () =>
            debounce(500, async (value: ApplicationSettingAuthentication) => {
                const isSuccess = await updateAppVersionConfig({
                    config: {
                        authentication: value
                    }
                })
            }),
        [updateAppVersionConfig]
    )

    useEffect(() => {
        const { unsubscribe } = watch((value, oldValue) => {
            const newAuthentication = clone(value)
            const roleSetting = (newAuthentication.perfect?.roleSetting ?? []).filter(role => roleIds.includes(role?.roleId ?? ''))
            handleSubmit(mergeDeepRight<ApplicationSettingAuthentication>(newAuthentication, { perfect: { roleSetting } }))
        })
        return unsubscribe
    }, [handleSubmit, roleIds, watch])

    const settingDetailEle = useMemo(() => {
        if (perfectMode === ApplicationSettingPerfectMode.ROLE) {
            return <PerfectGroupSetting />
        }
        return <PerfectFieldDetail prefix="perfect.commonSetting" />
    }, [perfectMode])

    const disabledWithVersion = useIsDisabledWithVersion()

    return (
        <SCxContainer>
            <SCxWrapper h="100%">
                <FormProvider {...methods}>
                    <CM.Title>登录设置</CM.Title>
                    <SettingBlock label="设计">
                        <Controller
                            name="logo"
                            control={control}
                            render={({ field }) => (
                                <UploadManageContext
                                    accept="image/*"
                                    {...uploadManagerInAppParams()}
                                    tokenRefreshManager={tokenRefreshManager}
                                >
                                    <Logo
                                        disabled={disabledWithVersion}
                                        {...field}
                                        onGetFullUrl={url => getImageFullUrlInApplication(appId, url)}
                                    />
                                </UploadManageContext>
                            )}
                        />
                        <Controller name="name" control={control} render={({ field }) => <Name label="标题" {...field} />} />
                        <Controller name="describe" control={control} render={({ field }) => <Name label="描述" {...field} />} />
                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                            <Text>背景</Text>
                            <Controller
                                name="background"
                                control={control}
                                render={({ field }) => (
                                    <Popover width={300} withinPortal position="left-start" autoUpdate={false} offsetOptions={152}>
                                        <Popover.Target>
                                            <FilledInput
                                                style={{ width: 180 }}
                                                value={field.value}
                                                onClear={() => {
                                                    field.onChange({ type: BACKGROUND_TYPE.color, color: undefined })
                                                }}
                                                getImageVariableUrl={parseVariable2String}
                                            />
                                        </Popover.Target>
                                        <FillPickerPopoverDropdown
                                            title="填充"
                                            value={field.value}
                                            onChange={field.onChange}
                                            enabledBackgroundTypes={[
                                                BACKGROUND_TYPE.color,
                                                BACKGROUND_TYPE.gradient,
                                                BACKGROUND_TYPE.image
                                            ]}
                                            uploadProps={uploadProps}
                                            {...colorSystemMethods}
                                        />
                                    </Popover>
                                )}
                            />
                        </ListItem4ByecodeUi>
                    </SettingBlock>
                    <SettingBlock label="登录方式">
                        <Flex direction="column" gap={12} px={8}>
                            <Controller
                                control={control}
                                name="login.phone.isOpened"
                                render={({ field }) => (
                                    <LoginBlock
                                        label={
                                            <Flex gap={8} alignItems="center">
                                                <Text> 短信验证码登录 </Text>
                                                {/* <SpaceGradeTag value={spaceVersionEnum.BASIC} /> */}
                                            </Flex>
                                        }
                                        checked={field.value}
                                        onChange={v => {
                                            // if (quota && quota?.currentVersionCode < spaceVersionEnum.BASIC) {
                                            //     openSpaceGradeConfirm('https://byecodehelp.yuque.com/org-wiki-byecodehelp-zavfcl/oevich/rbv29zp2qdk26iz2', 'BASIC')
                                            //     return
                                            // }
                                            field.onChange(v)
                                        }}
                                        icon={
                                            <TagIcon
                                                size={32}
                                                iconSize={20}
                                                radius={100}
                                                iconColor="var(--color-blue-500)"
                                                background="var(--color-blue-50)"
                                                icon="SmartphoneIconLibrary"
                                            />
                                        }
                                    >
                                        <AppSmsCard />
                                    </LoginBlock>
                                )}
                            />

                            <Controller
                                name="login.email.isOpened"
                                control={control}
                                render={({ field }) => (
                                    <LoginBlock
                                        label="邮箱登录"
                                        disableCollapse
                                        checked={field.value}
                                        onChange={field.onChange}
                                        icon={
                                            <TagIcon
                                                size={32}
                                                iconSize={20}
                                                radius={100}
                                                iconColor="var(--color-green-7)"
                                                background="var(--color-green-1)"
                                                icon="LetterIconLibrary"
                                            />
                                        }
                                    />
                                )}
                            />
                            <Controller
                                name="login.weChat"
                                control={control}
                                render={({ field }) => (
                                    <LoginWeChat
                                        value={field.value}
                                        label={
                                            <Flex gap={8} alignItems="center">
                                                <Text> 微信快捷登录 </Text>
                                                <SpaceGradeTag value={spaceVersionEnum.ADVANCED} />
                                            </Flex>
                                        }
                                        onChange={v => {
                                            if (
                                                quota &&
                                                quota?.currentVersionCode &&
                                                quota?.currentVersionCode < spaceVersionEnum.ADVANCED
                                            ) {
                                                openSpaceGradeConfirm(SpaceVersion.ADVANCED)
                                                return
                                            }
                                            field.onChange(v)
                                        }}
                                    />
                                )}
                            />
                            {/* <Controller
                                name="login.google"
                                control={control}
                                render={({ field }) => (
                                    <LoginBlock
                                        label="谷歌登录"
                                        checked={field.value.isOpened}
                                        onChange={v => field.onChange({...field.value, isOpened: v})}
                                        icon={
                                            <TagIcon
                                                size={32}
                                                iconSize={20}
                                                radius={100}
                                                iconColor="var(--color-green-500)"
                                                background="var(--color-green-50)"
                                                icon="LetterIconLibrary"
                                            />
                                        }
                                    >
                                        <Select />
                                    </LoginBlock>
                                )}
                            /> */}

                            <Controller
                                name="login.requireAgree.enable"
                                control={control}
                                render={({ field }) => (
                                    <Group
                                        defaultOpen={false}
                                        mode="switch"
                                        opened={field.value}
                                        onCollapseChange={v => {
                                            if (v) {
                                                !getValues('login.requireAgree.userProtocol.name') &&
                                                    setValue('login.requireAgree.userProtocol.name', '《用户协议》')

                                                !getValues('login.requireAgree.privacyPolicy.name') &&
                                                    setValue('login.requireAgree.privacyPolicy.name', '《隐私条款》')
                                            }
                                            field.onChange(v)
                                        }}
                                        label="登录前需同意用户协议和隐私条款"
                                        styles={{
                                            root: { padding: '0 !important' },
                                            label: { fontWeight: 'normal !important' },
                                            wrapper: { padding: '0 !important' },
                                            collapse: { padding: '0 !important' }
                                        }}
                                    >
                                        <Popover width={342} position="left-start" withinPortal offsetOptions={10}>
                                            <Popover.Target>
                                                <ListItem4ByecodeUi mx={-8} alignItems="center" justifyContent="space-between" enableHover>
                                                    <Text>用户协议</Text>
                                                    <Flex gap={4} alignItems="center">
                                                        <Text color="var(--color-gray-400)" size={12}>
                                                            点击设置
                                                        </Text>
                                                        <IconFont type="ArrowRightSmall" fill="var(--color-gray-400)" size={16} />
                                                    </Flex>
                                                </ListItem4ByecodeUi>
                                            </Popover.Target>
                                            <PopoverDropdownWithClose title="用户协议">
                                                <ListItem4ByecodeUi alignItems="center" justifyContent="space-between">
                                                    <Text>标题</Text>
                                                    <Input {...register('login.requireAgree.userProtocol.name')} />
                                                </ListItem4ByecodeUi>
                                                <ListItem4ByecodeUi alignItems="center" justifyContent="space-between">
                                                    <Text>链接</Text>
                                                    <Input placeholder="https://" {...register('login.requireAgree.userProtocol.url')} />
                                                </ListItem4ByecodeUi>
                                            </PopoverDropdownWithClose>
                                        </Popover>

                                        <Popover width={342} position="left-start" withinPortal offsetOptions={10}>
                                            <Popover.Target>
                                                <ListItem4ByecodeUi mx={-8} alignItems="center" justifyContent="space-between" enableHover>
                                                    <Text>隐私条款</Text>
                                                    <Flex gap={4} alignItems="center">
                                                        <Text color="var(--color-gray-400)" size={12}>
                                                            点击设置
                                                        </Text>
                                                        <IconFont type="ArrowRightSmall" fill="var(--color-gray-400)" size={16} />
                                                    </Flex>
                                                </ListItem4ByecodeUi>
                                            </Popover.Target>
                                            <PopoverDropdownWithClose title="隐私条款">
                                                <ListItem4ByecodeUi alignItems="center" justifyContent="space-between">
                                                    <Text>标题</Text>
                                                    <Input {...register('login.requireAgree.privacyPolicy.name')} />
                                                </ListItem4ByecodeUi>
                                                <ListItem4ByecodeUi alignItems="center" justifyContent="space-between">
                                                    <Text>链接</Text>
                                                    <Input placeholder="https://" {...register('login.requireAgree.privacyPolicy.url')} />
                                                </ListItem4ByecodeUi>
                                            </PopoverDropdownWithClose>
                                        </Popover>
                                    </Group>
                                )}
                            />
                            <Controller
                                name="register.enableRegister"
                                control={control}
                                render={({ field }) => (
                                    <SwitchControl
                                        enablePadding={false}
                                        label={
                                            <Flex gap={4}>
                                                <Text>是否允许注册用户</Text>
                                                <Tooltip
                                                    title={
                                                        <Text style={{ width: 244 }}>
                                                            关闭后，未注册用户无法通过登录自助注册，已有用户仍可登录。
                                                        </Text>
                                                    }
                                                >
                                                    <CM.Icon type="Question" size={16} />
                                                </Tooltip>
                                            </Flex>
                                        }
                                        checked={field.value !== false}
                                        onChange={e => field.onChange(e.target.checked)}
                                    />
                                )}
                            />
                        </Flex>
                    </SettingBlock>

                    <SettingBlock label="审核">
                        <Box px={8}>
                            <Controller
                                name="register.isReviewed"
                                control={control}
                                render={({ field }) => (
                                    <SwitchControl
                                        enablePadding={false}
                                        label={
                                            <Flex gap={4}>
                                                <Text>首次登录需审核</Text>
                                                <Tooltip
                                                    title={
                                                        <Text style={{ width: 244 }}>
                                                            开启后，用户首次登录需要管理员审核通过。
                                                            <p>注意：使用微信登录，不会触发审核机制。</p>
                                                        </Text>
                                                    }
                                                >
                                                    <CM.Icon type="Question" size={16} />
                                                </Tooltip>
                                            </Flex>
                                        }
                                        checked={field.value}
                                        onChange={e => field.onChange(e.target.checked)}
                                    />
                                )}
                            />
                        </Box>
                    </SettingBlock>
                    <Divider />
                    <Controller
                        name="perfect.isOpened"
                        control={control}
                        render={({ field }) => (
                            <Group
                                mode="switch"
                                opened={field.value}
                                onCollapseChange={field.onChange}
                                label={
                                    <Flex gap={4}>
                                        <Text>完善用户信息</Text>
                                        <Tooltip title="用户首次登录或注册后，需完善自身的用户信息">
                                            <CM.Icon type="Question" size={16} />
                                        </Tooltip>
                                    </Flex>
                                }
                            >
                                <Controller
                                    name="perfect.title"
                                    control={control}
                                    render={({ field }) => <Name label="完善信息标题" {...field} />}
                                />
                                <Controller
                                    name="perfect.describe"
                                    control={control}
                                    render={({ field }) => <Name label="完善信息描述" {...field} />}
                                />
                                {perfectMode === ApplicationSettingPerfectMode.ROLE && (
                                    <>
                                        <Controller
                                            name="perfect.selectRoleTitle"
                                            control={control}
                                            render={({ field }) => <Name label="选择角色标题" {...field} />}
                                        />
                                        <Controller
                                            name="perfect.selectRoleDescribe"
                                            control={control}
                                            render={({ field }) => <Name label="选择角色描述" {...field} />}
                                        />
                                    </>
                                )}
                                <Controller name="perfect.mode" control={control} render={({ field }) => <PerfectWay {...field} />} />
                                {settingDetailEle}
                            </Group>
                        )}
                    />
                </FormProvider>
            </SCxWrapper>
        </SCxContainer>
    )
}

export default SettingAuthentication
